import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Switch organization input schema
 */
const SwitchOrganizationInputSchema = z.object({
  organizationId: z.string().uuid("Invalid organization ID format"),
});

/**
 * Switch organization response schema
 */
const SwitchOrganizationResponseSchema = z.object({
  userId: z.string().uuid(),
  organizationId: z.string().uuid(),
  organizationName: z.string(),
});

const RequestPasswordResetInputSchema = z.object({
  email: z.string().email("Invalid email"),
});

const RequestPasswordResetResponseSchema = z.object({
  ok: z.literal(true),
});

const ResetPasswordInputSchema = z.object({
  token: z.string().min(32),
  newPassword: z.string().min(6),
});

const ResetPasswordResponseSchema = z.object({
  ok: z.literal(true),
});

const GoogleAuthUrlResponseSchema = z.object({
  // null when Google login is not configured, so the frontend can hide the button.
  authorizationUrl: z.string().nullable(),
});

const GoogleLoginInputSchema = z.object({
  code: z.string().min(1, "Code is required"),
  state: z.string().min(1, "State is required"),
});

const GoogleLoginResponseSchema = z.object({
  ok: z.literal(true),
});

/**
 * Auth Contracts
 *
 * `requestPasswordReset`, `resetPassword`, `googleAuthUrl`, and `googleLogin`
 * are reachable without an authenticated session — see the public-path
 * allowlist in `backend/src/api/routes/rpc/express.adapter.ts`. Keep that list
 * in sync if you rename or move these procedures.
 */
export const authContracts = {
  // Switch the user's active organization
  switchOrganization: oc
    .input(SwitchOrganizationInputSchema)
    .output(SwitchOrganizationResponseSchema)
    .route({
      method: "POST",
    }),
  requestPasswordReset: oc
    .input(RequestPasswordResetInputSchema)
    .output(RequestPasswordResetResponseSchema)
    .route({
      method: "POST",
    }),
  resetPassword: oc
    .input(ResetPasswordInputSchema)
    .output(ResetPasswordResponseSchema)
    .route({
      method: "POST",
    }),
  // Start "Sign in with Google": issues a CSRF state and returns the consent URL.
  googleAuthUrl: oc.output(GoogleAuthUrlResponseSchema).route({
    method: "GET",
  }),
  // Complete "Sign in with Google": exchange the code and establish the session.
  googleLogin: oc
    .input(GoogleLoginInputSchema)
    .output(GoogleLoginResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

// Export types for frontend use
export type SwitchOrganizationInput = z.infer<
  typeof SwitchOrganizationInputSchema
>;
export type SwitchOrganizationResponse = z.infer<
  typeof SwitchOrganizationResponseSchema
>;
export type RequestPasswordResetInput = z.infer<
  typeof RequestPasswordResetInputSchema
>;
export type RequestPasswordResetResponse = z.infer<
  typeof RequestPasswordResetResponseSchema
>;
export type ResetPasswordInput = z.infer<typeof ResetPasswordInputSchema>;
export type ResetPasswordResponse = z.infer<typeof ResetPasswordResponseSchema>;
export type GoogleAuthUrlResponse = z.infer<typeof GoogleAuthUrlResponseSchema>;
export type GoogleLoginInput = z.infer<typeof GoogleLoginInputSchema>;
export type GoogleLoginResponse = z.infer<typeof GoogleLoginResponseSchema>;
